
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { da } from "date-fns/locale";
import { supabase } from "@/lib/supabase";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { Download, Clock, Users, CalendarDays, Briefcase } from "lucide-react";
import * as XLSX from 'xlsx';

export const TimeOverview = () => {
  const { toast } = useToast();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [selectedMonth, setSelectedMonth] = useState((new Date().getMonth() + 1).toString());
  const [selectedProject, setSelectedProject] = useState<string>("");

  // Fetch user's monthly hours
  const { data: monthlyHours, isLoading: monthlyLoading, error: monthlyError } = useQuery({
    queryKey: ['monthly-hours', selectedYear, selectedMonth],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      const { data, error } = await supabase.rpc('get_monthly_hours', {
        year: parseInt(selectedYear),
        month: parseInt(selectedMonth),
        user_id: session.user.id
      });

      if (error) {
        console.error('Error fetching monthly hours:', error);
        throw error;
      }

      // Return the first row or a default object if no data
      return data?.[0] || { 
        total_hours: 0,
        regular_hours: 0,
        overtime_hours: 0
      };
    },
  });

  // Fetch available projects
  const { data: projects } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .order('title');

      if (error) throw error;
      return data;
    }
  });

  // Fetch project hours when a project is selected
  const { data: projectHours, isLoading: projectLoading } = useQuery({
    queryKey: ['project-hours', selectedProject],
    queryFn: async () => {
      if (!selectedProject) return null;
      
      const { data, error } = await supabase.rpc('get_project_hours', {
        project_id: selectedProject
      });

      if (error) {
        console.error('Error fetching project hours:', error);
        throw error;
      }
      return data;
    },
    enabled: !!selectedProject
  });

  // Fetch task summaries for selected project
  const { data: taskSummaries } = useQuery({
    queryKey: ['task-summaries', selectedProject],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('task_time_summary')
        .select('*')
        .eq('project_id', selectedProject);

      if (error) throw error;
      return data;
    },
    enabled: !!selectedProject
  });

  const exportToExcel = () => {
    try {
      const workbook = XLSX.utils.book_new();
      
      // Monthly Overview Sheet
      if (monthlyHours) {
        const monthlyData = [[
          'Periode',
          'Normale Timer',
          'Overtid',
          'Total'
        ], [
          `${selectedMonth}/${selectedYear}`,
          monthlyHours.regular_hours,
          monthlyHours.overtime_hours,
          monthlyHours.total_hours
        ]];
        
        const monthlySheet = XLSX.utils.aoa_to_sheet(monthlyData);
        XLSX.utils.book_append_sheet(workbook, monthlySheet, "Månedsoversigt");
      }

      // Project Hours Sheet
      if (projectHours) {
        const projectData = [
          ['Medarbejder', 'Timer'],
          ...projectHours.map(ph => [`${ph.first_name} ${ph.last_name}`, ph.total_hours])
        ];
        
        const projectSheet = XLSX.utils.aoa_to_sheet(projectData);
        XLSX.utils.book_append_sheet(workbook, projectSheet, "Projekttimer");
      }

      // Task Summary Sheet
      if (taskSummaries) {
        const taskData = [
          ['Opgave', 'Estimerede Timer', 'Faktiske Timer', 'Antal Medarbejdere'],
          ...taskSummaries.map(ts => [
            ts.task_title,
            ts.estimated_hours,
            ts.total_hours,
            ts.total_workers
          ])
        ];
        
        const taskSheet = XLSX.utils.aoa_to_sheet(taskData);
        XLSX.utils.book_append_sheet(workbook, taskSheet, "Opgaveoversigt");
      }

      // Generate filename with current date
      const fileName = `tidsoversigt_${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      toast({
        title: "Excel-fil eksporteret",
        description: "Din tidsoversigt er blevet downloadet",
      });
    } catch (error) {
      console.error('Fejl ved Excel-eksport:', error);
      toast({
        title: "Fejl ved eksport",
        description: "Der opstod en fejl ved generering af Excel-filen",
        variant: "destructive"
      });
    }
  };

  if (monthlyError) {
    toast({
      title: "Fejl ved indlæsning",
      description: "Der opstod en fejl ved hentning af timedata",
      variant: "destructive"
    });
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Tidsoversigt</h2>
        <Button onClick={exportToExcel} className="flex items-center gap-2">
          <Download className="h-4 w-4" />
          Eksportér til Excel
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Periode</CardTitle>
            <CalendarDays className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className="w-[120px]">
                  <SelectValue placeholder="Måned" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                    <SelectItem key={month} value={month.toString()}>
                      {format(new Date(2024, month - 1, 1), 'MMMM', { locale: da })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-[100px]">
                  <SelectValue placeholder="År" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((year) => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Mine Timer</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {monthlyLoading ? (
              <div className="text-sm text-muted-foreground">Indlæser...</div>
            ) : monthlyHours ? (
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Normal tid:</span>
                  <span className="font-medium">{monthlyHours.regular_hours} timer</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Overtid:</span>
                  <span className="font-medium">{monthlyHours.overtime_hours} timer</span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t">
                  <span className="text-sm font-medium">Total:</span>
                  <span className="font-bold">{monthlyHours.total_hours} timer</span>
                </div>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">Ingen data tilgængelig</div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Projekt</CardTitle>
            <Briefcase className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <Select value={selectedProject} onValueChange={setSelectedProject}>
              <SelectTrigger>
                <SelectValue placeholder="Vælg projekt" />
              </SelectTrigger>
              <SelectContent>
                {projects?.map((project) => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      </div>

      {selectedProject && (
        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList>
            <TabsTrigger value="overview">Projektoverblik</TabsTrigger>
            <TabsTrigger value="tasks">Opgaver</TabsTrigger>
            <TabsTrigger value="employees">Medarbejdere</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex items-center gap-4 p-4 rounded-lg bg-muted">
                      <Clock className="h-8 w-8 text-primary" />
                      <div>
                        <p className="text-sm font-medium">Samlet tid</p>
                        <p className="text-2xl font-bold">
                          {projectHours?.reduce((sum, ph) => sum + Number(ph.total_hours), 0) || 0} timer
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4 p-4 rounded-lg bg-muted">
                      <Users className="h-8 w-8 text-primary" />
                      <div>
                        <p className="text-sm font-medium">Medarbejdere</p>
                        <p className="text-2xl font-bold">{projectHours?.length || 0}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 p-4 rounded-lg bg-muted">
                      <Briefcase className="h-8 w-8 text-primary" />
                      <div>
                        <p className="text-sm font-medium">Opgaver</p>
                        <p className="text-2xl font-bold">{taskSummaries?.length || 0}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="tasks">
            <Card>
              <CardContent className="pt-6">
                <div className="relative overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead className="text-xs uppercase bg-muted">
                      <tr>
                        <th className="px-6 py-3">Opgave</th>
                        <th className="px-6 py-3">Estimeret</th>
                        <th className="px-6 py-3">Brugt</th>
                        <th className="px-6 py-3">Difference</th>
                        <th className="px-6 py-3">Medarbejdere</th>
                      </tr>
                    </thead>
                    <tbody>
                      {taskSummaries?.map((task) => (
                        <tr key={task.task_id} className="border-b">
                          <td className="px-6 py-4 font-medium">{task.task_title}</td>
                          <td className="px-6 py-4">{task.estimated_hours} timer</td>
                          <td className="px-6 py-4">{task.total_hours} timer</td>
                          <td className="px-6 py-4">
                            {(task.total_hours - task.estimated_hours).toFixed(1)} timer
                          </td>
                          <td className="px-6 py-4">{task.total_workers}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="employees">
            <Card>
              <CardContent className="pt-6">
                <div className="relative overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead className="text-xs uppercase bg-muted">
                      <tr>
                        <th className="px-6 py-3">Medarbejder</th>
                        <th className="px-6 py-3">Timer i alt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {projectHours?.map((employee) => (
                        <tr key={employee.user_id} className="border-b">
                          <td className="px-6 py-4 font-medium">
                            {employee.first_name} {employee.last_name}
                          </td>
                          <td className="px-6 py-4">{employee.total_hours} timer</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};
